/*
  Nav 2026 behaviour for promgirl.com.

  Loaded with defer from sections/header.liquid when settings.nav_2026_enabled is on,
  after assets/component-drawer-menu.js, so Taiga's own <drawer-menu> custom element is
  already defined by the time anything here runs. The drawer shell itself (open, close,
  overlay, focus trap, scroll lock) stays Taiga's; this file only adds the 2026 layer.

  The file is divided into regions, one per follow up task, each fenced by a
  "#region" / "#endregion" banner and padded from its neighbours. Keep the banners and
  the padding: they are what stops two agents working in parallel from landing in the
  same merge hunk.

    config-and-utils  shared namespace, helpers, tracking transport
    drawer            phone drill in panels, Back button, accordions  (Task 7)
    search            header field handing off to #drawer-search      (Task 9)
    collapse          phone search row collapsing on scroll           (Task 10)
    desktop           hover intent, keyboard opening, focus handling  (Task 14)
    tracking          nav_click capture phase listener                (Task 16)
    init              bootstrap
*/
(function () {
  'use strict';

  // #region config-and-utils

  var PG_NAV = {
    version: '0.1.0',
    HOVER_OPEN_MS: 300,
    HOVER_CLOSE_MS: 180,
    HOVER_SWITCH_MS: 60,
    PANEL_TRANSITION_MS: 260,
    SCROLL_COLLAPSE_PX: 80,
    selectors: {
      drawer: '#drawer-menu',
      drawerRoot: '[data-pg-nav-root]',
      searchDrawer: '#drawer-search',
      searchDrawerInput: '#drawerSearchInput',
      searchField: '[data-pg-search-field]',
      searchRow: '[data-pg-search-row]',
      desktopNav: '[data-pg-desktop-nav]',
      redirectMap: '#pg-search-redirects'
    }
  };

  function qs(selector, root) {
    return (root || document).querySelector(selector);
  }

  function qsa(selector, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  function reducedMotion() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (err) {
      return false;
    }
  }

  function track(eventName, params) {
    // Measured 2026-09-22 against GTM-5CSNL6BP: window.gtag on this store is
    // GTM's arguments shim. Calling it lands ["event", name, {...}] in the
    // dataLayer, GTM reads the event name but never the parameters (Data
    // Layer Variables for nav_label etc. resolve undefined). A named object
    // push carries both, so that is the only path used. (Deviation from D10,
    // which said gtag first.)
    if (!eventName) { return; }
    var payload = params || {};
    try {
      var entry = { event: eventName };
      for (var key in payload) {
        if (Object.prototype.hasOwnProperty.call(payload, key)) { entry[key] = payload[key]; }
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(entry);
    } catch (err) { /* tracking must never break navigation */ }
  }

  PG_NAV.qs = qs;
  PG_NAV.qsa = qsa;
  PG_NAV.reducedMotion = reducedMotion;
  PG_NAV.track = track;

  window.PG_NAV = PG_NAV;

  // #endregion config-and-utils

  //
  //
  //
  //
  //
  //

  // #region drawer

  /*
    The phone menu's drill in panels, ported from the Avery Austin engine.

    Taiga's <drawer-menu> still owns open, close, overlay, Escape, focus trap and scroll
    lock; this only watches its is-open class and manages what is inside. Panels ship as
    <template> elements and are stamped on first open, so the closed drawer is 88
    elements instead of 881.

    Exactly one subtree is rendered at a time: whatever slides out is marked inert at
    once and parked with display:none when the transition ends. That keeps the tab order,
    Taiga's focus trap and any accessibility scan looking only at what is on screen.

    History: one entry when the drawer opens, one more per panel. Back steps out of a
    panel before it closes the menu, and following a link unwinds the entries first so
    Back on the next page goes to the previous page, not back into the menu.
  */
  function initDrawer() {
    var drawer = qs(PG_NAV.selectors.drawer);
    if (!drawer) { return; }

    var root = qs(PG_NAV.selectors.drawerRoot, drawer);
    var stage = qs('[data-pg-panels]', drawer);
    if (!root || !stage) { return; }

    var activePanel = null;
    var pushed = 0;
    var pendingUrl = null;
    // The Back button steps out of a panel AND drops the history entry that panel
    // added. That pop is ours, not the shopper pressing Back, so popstate has to let
    // it through untouched or it would read as a second Back and close the menu.
    var ownPop = false;

    function slideMs() { return reducedMotion() ? 0 : PG_NAV.PANEL_TRANSITION_MS; }

    function setInert(el, on) {
      try { el.inert = on; } catch (err) { /* older browsers: parking covers it */ }
    }

    function unpark(el) {
      el.classList.remove('pg-parked');
      el.removeAttribute('aria-hidden');
      setInert(el, false);
    }

    function panelFor(handle) {
      var panel = stage.querySelector('[data-pg-panel="' + handle + '"]');
      if (panel) { return panel; }
      var tpl = stage.querySelector('template[data-pg-panel-template="' + handle + '"]');
      if (!tpl) { return null; }
      stage.appendChild(document.importNode(tpl.content, true));
      panel = stage.querySelector('[data-pg-panel="' + handle + '"]');
      if (panel) { panel.classList.add('pg-parked'); }
      return panel;
    }

    function syncOpeners(handle) {
      qsa('[data-pg-open]', drawer).forEach(function (btn) {
        btn.setAttribute('aria-expanded', btn.getAttribute('data-pg-open') === handle ? 'true' : 'false');
      });
    }

    function hide(panel) {
      panel.classList.remove('is-active');
      setInert(panel, true);
      window.setTimeout(function () {
        if (panel === activePanel) { return; }
        panel.classList.add('pg-parked');
        panel.setAttribute('aria-hidden', 'true');
      }, slideMs());
    }

    function showPanel(handle, fromHistory) {
      var panel = panelFor(handle);
      if (!panel || panel === activePanel) { return; }
      var leaving = activePanel;
      activePanel = panel;

      setInert(root, true);
      root.classList.add('is-behind');

      unpark(panel);
      void panel.offsetWidth;
      panel.classList.add('is-active');
      if (leaving) { hide(leaving); }
      syncOpeners(handle);

      var scroller = panel.querySelector('[data-pg-panel-scroll]');
      if (scroller) { scroller.scrollTop = 0; }

      window.setTimeout(function () {
        if (activePanel !== panel) { return; }
        var back = panel.querySelector('[data-pg-back]');
        if (back) { back.focus({ preventScroll: true }); }
        root.classList.add('pg-parked');
        root.setAttribute('aria-hidden', 'true');
      }, slideMs());

      if (!fromHistory) {
        history.pushState({ pgNav: 'panel' }, '');
        pushed++;
      }
    }

    function showRoot(fromHistory, skipFocus) {
      if (!activePanel) { return; }
      var leaving = activePanel;
      var handle = leaving.getAttribute('data-pg-panel');
      activePanel = null;

      unpark(root);
      root.classList.remove('is-behind');
      hide(leaving);
      syncOpeners('');

      if (!skipFocus) {
        var opener = drawer.querySelector('[data-pg-open="' + handle + '"]');
        if (opener) { opener.focus({ preventScroll: true }); }
      }
      if (!fromHistory && pushed > 0) {
        pushed--;
        ownPop = true;
        history.back();
      }
    }

    function closeDrawer() {
      var btn = root.querySelector('[data-close]');
      if (btn) { btn.click(); }
    }

    // One open group per panel. The unheaded group has no button, so it always shows.
    function toggleAccordion(btn) {
      var group = btn.closest('[data-pg-acc]');
      if (!group) { return; }
      var willOpen = !group.classList.contains('is-open');
      qsa('[data-pg-acc].is-open', group.parentNode).forEach(function (other) {
        other.classList.remove('is-open');
        var otherBtn = other.querySelector('.pg-acc__btn');
        if (otherBtn) { otherBtn.setAttribute('aria-expanded', 'false'); }
      });
      if (willOpen) {
        group.classList.add('is-open');
        btn.setAttribute('aria-expanded', 'true');
      }
    }

    drawer.addEventListener('click', function (evt) {
      var back = evt.target.closest('[data-pg-back]');
      if (back) { evt.preventDefault(); showRoot(); return; }

      var opener = evt.target.closest('[data-pg-open]');
      if (opener) { evt.preventDefault(); showPanel(opener.getAttribute('data-pg-open')); return; }

      // A panel's own close button is a clone, so Woolman never bound it.
      var closer = evt.target.closest('[data-close]');
      if (closer && stage.contains(closer)) { evt.preventDefault(); closeDrawer(); return; }

      var accBtn = evt.target.closest('.pg-acc__btn');
      if (accBtn) { toggleAccordion(accBtn); return; }

      var anchor = evt.target.closest('a[href]');
      if (anchor && pushed > 0 && evt.button === 0 && anchor.target !== '_blank' &&
          !evt.metaKey && !evt.ctrlKey && !evt.shiftKey && !evt.altKey) {
        evt.preventDefault();
        pendingUrl = anchor.href;
        var steps = pushed;
        pushed = 0;
        history.go(-steps);
        // popstate normally lands within a frame; this is the safety net if it does not.
        window.setTimeout(function () {
          if (!pendingUrl) { return; }
          var url = pendingUrl;
          pendingUrl = null;
          window.location.assign(url);
        }, 400);
      }
    });

    window.addEventListener('popstate', function () {
      if (ownPop) { ownPop = false; return; }
      if (pendingUrl) {
        var url = pendingUrl;
        pendingUrl = null;
        window.location.assign(url);
        return;
      }
      if (!drawer.classList.contains('is-open') || pushed === 0) { return; }
      pushed--;
      if (activePanel) { showRoot(true); } else { closeDrawer(); }
    });

    var wasOpen = drawer.classList.contains('is-open');

    function onOpen() {
      history.pushState({ pgNav: 'drawer' }, '');
      pushed++;
      var current = root.querySelector('.pg-nav__l1[aria-current="page"]');
      if (current) { current.scrollIntoView({ block: 'nearest' }); }
    }

    function onClose() {
      if (activePanel) { showRoot(true, true); }
      if (pushed > 0) {
        var steps = pushed;
        pushed = 0;
        history.go(-steps);
      }
    }

    new MutationObserver(function () {
      var isOpen = drawer.classList.contains('is-open');
      if (isOpen === wasOpen) { return; }
      wasOpen = isOpen;
      if (isOpen) { onOpen(); } else { onClose(); }
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  // #endregion drawer

  //
  //
  //
  //
  //
  //

  // #region search

  /*
    Task 9 (D8). The header search field is a real form. On a pointer press it hands
    off to Taiga's existing #drawer-search: open the drawer through
    Woolman.ModalsAndDrawers, copy anything already typed into #drawerSearchInput,
    dispatch an input event so predictive search runs, and focus it. With JS off, or
    when Woolman or the drawer is missing, nothing is intercepted and the form posts
    to /search exactly as it does today.

    Keyboard focus is deliberately left alone. Opening a dialog the moment a field
    receives focus is a change of context on focus and fails WCAG SC 3.2.1 (Level A),
    which this store cannot afford. Keyboard users get a real field they can type in
    and submit, plus ArrowDown as an explicit opener for the drawer.
  */
  /*
    Task 12 (D9). The category redirect map. Terms the customer would be better served
    by a collection than by a results page are listed in snippets/pg-search-redirects
    .liquid; on submit of ANY search form, an exact normalised hit sends them there
    instead. Nothing outside that file can be redirected, and the hook never fires on
    an autocomplete selection: predictive search handles Enter on a highlighted option
    itself, and the input carries aria-activedescendant while one is highlighted.
  */
  function initRedirects() {
    var node = qs(PG_NAV.selectors.redirectMap);
    if (!node) { return; }

    var map;
    try {
      map = JSON.parse(node.textContent || '{}');
    } catch (err) {
      return;
    }
    if (!map || typeof map !== 'object') { return; }

    function normalise(term) {
      return String(term === null || term === undefined ? '' : term)
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
    }

    function targetFor(term) {
      var t = normalise(term);
      if (!t) { return null; }
      if (Object.prototype.hasOwnProperty.call(map, t)) { return map[t]; }

      /* An unlisted plural resolves only when its singular IS one of the listed terms. */
      var candidates = [];
      if (t.length > 3 && t.slice(-2) === 'es') { candidates.push(t.slice(0, -2)); }
      if (t.length > 2 && t.slice(-1) === 's') { candidates.push(t.slice(0, -1)); }
      for (var i = 0; i < candidates.length; i++) {
        if (Object.prototype.hasOwnProperty.call(map, candidates[i])) { return map[candidates[i]]; }
      }
      return null;
    }

    function onSubmit(evt) {
      if (evt.defaultPrevented) { return; }

      var form = evt.currentTarget;
      var input = qs('input[name="q"]', form);
      if (!input) { return; }
      if (input.getAttribute('aria-activedescendant')) { return; }

      var url = targetFor(input.value);
      if (!url) { return; }

      evt.preventDefault();
      track('search_redirect', { search_term: normalise(input.value), search_target: url });
      window.location.assign(url);
    }

    var forms = qsa('form[role="search"]');
    for (var f = 0; f < forms.length; f++) {
      forms[f].addEventListener('submit', onSubmit);
    }
  }

  function initSearch() {
    initRedirects();

    var fields = qsa(PG_NAV.selectors.searchField);
    if (!fields.length) { return; }

    var POINTER_WINDOW_MS = 400;
    var RETURN_GUARD_MS = 800;
    var lastPointerAt = 0;
    var suppressUntil = 0;
    var returnFocusTo = null;

    function searchDrawer() {
      return qs(PG_NAV.selectors.searchDrawer);
    }

    function drawerApi() {
      var W = window.Woolman;
      if (!W || !W.ModalsAndDrawers) { return null; }
      return typeof W.ModalsAndDrawers.showModalOrDrawer === 'function' ? W.ModalsAndDrawers : null;
    }

    function surfaceFor(variant) {
      if (variant === 'desktop') { return 'header-desktop'; }
      if (variant === 'drawer') { return 'drawer'; }
      return 'header-phone';
    }

    function handOff(input, variant, byKeyboard) {
      var drawer = searchDrawer();
      var api = drawerApi();
      if (!drawer || !api) { return false; }
      if (drawer.classList.contains('is-open')) { return false; }

      api.showModalOrDrawer('drawer-search');

      var target = qs(PG_NAV.selectors.searchDrawerInput);
      if (target) {
        var typed = input ? input.value : '';
        if (typed && target.value !== typed) {
          target.value = typed;
          try {
            target.dispatchEvent(new Event('input', { bubbles: true }));
          } catch (err) { /* the drawer still opens without predictive results */ }
        }
        /*
          Focus inside the gesture so a phone keyboard stays up. Taiga's own
          showModalOrDrawer focuses the same input again at 600ms, which is a no-op.
        */
        try { target.focus(); } catch (err2) { /* focus is best effort */ }
        /*
          Select a carried-over term (the results page fills the header field with
          it) so typing replaces it instead of appending to it (Nicole, Oct 20).
        */
        if (target.value) {
          try { target.select(); } catch (err3) { /* selection is best effort */ }
        }
      }

      /*
        Only a keyboard opener gets focus back on close. A pointer user who closed
        the panel and found the caret in the header field typed there in place,
        while a click on the same field opened the panel: two behaviors for one
        field (Nicole, Oct 20). Pointer users now always land in the panel.
      */
      returnFocusTo = byKeyboard ? (input || null) : null;
      if (!byKeyboard && input) {
        try { input.blur(); } catch (err4) { /* blur is best effort */ }
      }
      track('search_open', { search_surface: surfaceFor(variant) });
      return true;
    }

    /*
      Taiga restores focus to whatever opened a drawer, but our synchronous focus
      means the trap never saw the header field, so put focus back by hand. The guard
      window stops that restored focus from opening the drawer all over again.
    */
    function watchClose() {
      var drawer = searchDrawer();
      if (!drawer || typeof window.MutationObserver !== 'function') { return; }
      var observer = new MutationObserver(function () {
        if (drawer.classList.contains('is-open')) { return; }
        if (!returnFocusTo) { return; }
        var el = returnFocusTo;
        returnFocusTo = null;
        suppressUntil = Date.now() + RETURN_GUARD_MS;
        if (el.isConnected === false) { return; }
        try { el.focus(); } catch (err) { /* focus is best effort */ }
      });
      observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    function bindField(form) {
      var variant = form.getAttribute('data-pg-search-field') || 'phone';
      var input = qs('input[type="search"]', form);
      if (!input) { return; }

      if (variant === 'drawer') {
        /* Already inside the menu drawer: no hand off, only the engagement event. */
        input.addEventListener('focus', function () {
          track('search_open', { search_surface: 'drawer' });
        }, { once: true });
        return;
      }

      input.addEventListener('pointerdown', function () {
        lastPointerAt = Date.now();
      }, true);

      input.addEventListener('focus', function (evt) {
        if (Date.now() < suppressUntil) { return; }
        if (Date.now() - lastPointerAt > POINTER_WINDOW_MS) { return; }
        if (handOff(input, variant) && evt.cancelable) { evt.preventDefault(); }
      });

      input.addEventListener('click', function (evt) {
        if (Date.now() < suppressUntil) { return; }
        lastPointerAt = Date.now();
        if (handOff(input, variant) && evt.cancelable) { evt.preventDefault(); }
      });

      input.addEventListener('keydown', function (evt) {
        if (evt.key !== 'ArrowDown' && evt.key !== 'Down') { return; }
        if (handOff(input, variant, true)) { evt.preventDefault(); }
      });

      var submit = qs('.pg-search__submit', form);
      if (submit) {
        submit.addEventListener('click', function (evt) {
          /*
            An empty header field means the customer wants the search panel, not a
            search for nothing. A typed term submits to /search as normal.
          */
          if (input.value.trim() !== '') { return; }
          if (handOff(input, variant)) { evt.preventDefault(); }
        });
      }
    }

    watchClose();

    for (var i = 0; i < fields.length; i++) {
      bindField(fields[i]);
    }
  }

  // #endregion search

  //
  //
  //
  //
  //
  //

  // #region collapse

  /*
    Task 10 (D7). The phone search row collapses after SCROLL_COLLAPSE_PX of downward
    scroll and returns on any upward scroll. All this does is toggle
    html.pg-search-collapsed; the height, the transition and the visibility live in
    pg-nav-styles.liquid so the row cannot flash before the stylesheet arrives. The
    field stays in the DOM the whole time, so nothing typed into it is lost.
  */
  function initCollapse() {
    var row = qs(PG_NAV.selectors.searchRow);
    if (!row) { return; }

    var root = document.documentElement;
    var DEADZONE_PX = 2;
    var lastY = window.scrollY || window.pageYOffset || 0;
    var collapsed = false;
    var queued = false;

    function setCollapsed(next) {
      if (next === collapsed) { return; }
      collapsed = next;
      root.classList.toggle('pg-search-collapsed', next);
    }

    function read() {
      queued = false;
      var y = window.scrollY || window.pageYOffset || 0;
      var delta = y - lastY;

      if (y <= PG_NAV.SCROLL_COLLAPSE_PX) {
        setCollapsed(false);
        lastY = y;
        return;
      }

      if (delta > DEADZONE_PX) {
        setCollapsed(true);
      } else if (delta < -DEADZONE_PX) {
        setCollapsed(false);
      }

      if (delta > DEADZONE_PX || delta < -DEADZONE_PX) { lastY = y; }
    }

    window.addEventListener('scroll', function () {
      if (queued) { return; }
      queued = true;
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(read);
      } else {
        read();
      }
    }, { passive: true });
  }

  // #endregion collapse

  //
  //
  //
  //
  //
  //

  // #region desktop

  /*
    Task 14 (fact 11). The desktop bar, ported from the Avery Austin engine.

    Hover intent: 300ms before the first panel opens, 60ms to switch once one is already
    open, 180ms before a panel closes on the way out. The first delay is what stops a
    panel flashing open while somebody drags the pointer across the bar on the way to the
    cart; the switch delay is short because once a panel is open the shopper has already
    said they want the menu.

    The hover half is gated on (hover: hover) and (pointer: fine). A touch screen gets
    first tap opens, second tap follows the link, because a tap that both opens a panel
    and navigates away is the classic phantom mega menu.

    The keyboard half is the part the old bar never had: its top level was a
    summary[role=link] with an onclick, so Enter navigated and nothing opened a panel at
    all. Here ArrowDown or Space opens and moves focus to the first link inside, Escape
    closes and puts focus back on the bar link, and focus leaving the item closes it.
  */
  function initDesktop() {
    var nav = qs(PG_NAV.selectors.desktopNav);
    if (!nav) { return; }

    var items = qsa('[data-pg-item]', nav);
    if (!items.length) { return; }

    var openTimer = null;
    var closeTimer = null;
    var canHover = false;
    try {
      canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    } catch (err) { /* no matchMedia: treat as touch, which is the safe direction */ }

    function linkOf(item) { return item.querySelector('.pg-nav__link'); }

    function close(item) {
      item.classList.remove('is-open');
      var link = linkOf(item);
      if (link) { link.setAttribute('aria-expanded', 'false'); }
    }

    function closeAll() {
      for (var i = 0; i < items.length; i++) { close(items[i]); }
    }

    function open(item) {
      for (var i = 0; i < items.length; i++) {
        if (items[i] !== item) { close(items[i]); }
      }
      item.classList.add('is-open');
      var link = linkOf(item);
      if (link) { link.setAttribute('aria-expanded', 'true'); }
    }

    function openItem() {
      for (var i = 0; i < items.length; i++) {
        if (items[i].classList.contains('is-open')) { return items[i]; }
      }
      return null;
    }

    function bind(item) {
      var link = linkOf(item);
      if (!link) { return; }

      if (canHover) {
        item.addEventListener('mouseenter', function () {
          window.clearTimeout(closeTimer);
          window.clearTimeout(openTimer);
          var delay = openItem() ? PG_NAV.HOVER_SWITCH_MS : PG_NAV.HOVER_OPEN_MS;
          openTimer = window.setTimeout(function () { open(item); }, delay);
        });
        item.addEventListener('mouseleave', function () {
          window.clearTimeout(openTimer);
          closeTimer = window.setTimeout(function () { close(item); }, PG_NAV.HOVER_CLOSE_MS);
        });
      } else {
        link.addEventListener('click', function (evt) {
          if (!item.classList.contains('is-open')) {
            evt.preventDefault();
            open(item);
          }
        });
      }

      link.addEventListener('keydown', function (evt) {
        if (evt.key !== 'ArrowDown' && evt.key !== 'Down' && evt.key !== ' ' && evt.key !== 'Spacebar') { return; }
        evt.preventDefault();
        open(item);
        var first = item.querySelector('.pg-mega a[href], .pg-mega button');
        if (first) { first.focus({ preventScroll: true }); }
      });

      /*
        focusout fires before the new element takes focus, so relatedTarget is where
        focus is going. Tabbing out of the last link in a panel closes it; tabbing
        between two links inside it does not.
      */
      item.addEventListener('focusout', function (evt) {
        if (!evt.relatedTarget || !item.contains(evt.relatedTarget)) { close(item); }
      });
    }

    for (var i = 0; i < items.length; i++) { bind(items[i]); }

    document.addEventListener('keydown', function (evt) {
      if (evt.key !== 'Escape' && evt.key !== 'Esc') { return; }
      var item = openItem();
      if (!item) { return; }
      var hadFocus = item.contains(document.activeElement);
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
      closeAll();
      if (hadFocus) {
        var link = linkOf(item);
        if (link) { link.focus({ preventScroll: true }); }
      }
    });

    document.addEventListener('click', function (evt) {
      if (nav.contains(evt.target)) { return; }
      window.clearTimeout(openTimer);
      closeAll();
    });
  }

  // #endregion desktop

  //
  //
  //
  //
  //
  //

  // #region tracking

  /*
    Task 16 (D10). One capture phase click listener on the document, reading the
    data-pg-track, data-pg-level and data-pg-parent attributes the three nav snippets
    stamp on every row they render, and sending nav_click through PG_NAV.track.

    Capture phase for two reasons. The drawer's own handler calls preventDefault on a
    link click so it can unwind its pushed history entries before navigating, and a
    bubble listener behind that would still fire but would be racing the navigation;
    capture runs first, before anything can stop propagation. It also means a click on
    something inside a row, a swatch disc or an icon, still resolves to the row through
    closest().

    The surface is read from the DOM rather than stamped in Liquid, because
    pg-nav-panel renders the same markup on both, and a drawer panel only exists once
    pg-nav.js has cloned it into the drawer.

    Nothing here can break a click: PG_NAV.track swallows its own errors, and the whole
    call is in a try/catch besides.
  */
  function initTracking() {
    if (!document.body) { return; }

    document.addEventListener('click', function (evt) {
      try {
        var target = evt.target;
        if (!target || typeof target.closest !== 'function') { return; }

        var el = target.closest('[data-pg-track]');
        if (!el) { return; }

        var label = el.getAttribute('data-pg-track');
        if (!label) { return; }

        var surface = 'other';
        if (el.closest(PG_NAV.selectors.drawer)) {
          surface = 'drawer';
        } else if (el.closest(PG_NAV.selectors.desktopNav)) {
          surface = 'desktop';
        }

        track('nav_click', {
          nav_label: label,
          nav_level: el.getAttribute('data-pg-level') || '',
          nav_parent: el.getAttribute('data-pg-parent') || '',
          nav_surface: surface
        });
      } catch (err) { /* tracking must never break navigation */ }
    }, true);
  }

  // #endregion tracking

  //
  //
  //
  //
  //
  //

  // #region init

  function init() {
    var starters = [initDrawer, initSearch, initCollapse, initDesktop, initTracking];
    for (var i = 0; i < starters.length; i++) {
      try {
        starters[i]();
      } catch (err) {
        if (window.console && window.console.warn) {
          window.console.warn('pg-nav: init step failed', err);
        }
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // #endregion init
})();
